import React from 'react'
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@hapana/shared/web/services/gqty'
import { formatTimeAgo } from '@hapana/shared/utils/date.util'
import { useMe } from '../../../hooks/me.hooks'
import { useSupportAssistant } from '../support-assistant.hooks'

interface RecentChat {
  supportChatId: string
  previewText: string
  updatedAt: string
  status: 'ACTIVE' | 'ENDED'
}

interface RecentChatsProps {
  onViewAllClick: () => void
  fullWidth?: boolean
}

export function RecentChats({ onViewAllClick, fullWidth = false }: RecentChatsProps) {
  const { t } = useTranslation('core')
  const { loadChat } = useSupportAssistant()
  const { data: meData } = useMe() as { data: any }
  const userId = meData?.me?.id

  // Query recent chats using GQty
  const query = useQuery({ suspense: false }) as any
  const allRecentChats = userId
    ? (query.userSupportChats({
        userId,
        pagination: { limit: 1 },
      }) as RecentChat[] | undefined)
    : undefined

  // Explicitly select fields to ensure GQty fetches them
  allRecentChats?.forEach((chat: any) => {
    chat.supportChatId
    chat.previewText
    chat.updatedAt
    chat.createdAt
    chat.status
  })

  // Filter out chats with undefined/null supportChatId
  const recentChats = allRecentChats?.filter((chat) => chat?.supportChatId) || []

  // Hide if no user ID or no chats
  if (!userId || recentChats.length === 0) {
    return null
  }

  const handleChatClick = (chatId: string, chatTitle: string) => {
    loadChat(chatId, chatTitle)
  }

  return (
    <Box sx={{ px: fullWidth ? 0 : '24px', py: 0.75, minHeight: 64 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
          px: 0.75,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}
        >
          {t('supportAssistant.recentChats.title')}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={onViewAllClick}
          data-testid="recent-chats-view-all"
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontSize: '0.75rem',
            minWidth: 'auto',
            px: 0,
            py: 0.25,
            minHeight: 'auto',
            mr: -0.5,
          }}
        >
          {t('supportAssistant.recentChats.viewAll')}
        </Button>
      </Box>

      {/* Chat List */}
      <List disablePadding>
        {recentChats.map((chat) => {
          const timeAgo = formatTimeAgo(chat.updatedAt)

          const previewText = chat.previewText
            ? chat.previewText.length > 35
              ? `${chat.previewText.substring(0, 35)}...`
              : chat.previewText
            : t('supportAssistant.recentChats.noPreview')

          return (
            <ListItem key={chat.supportChatId} disablePadding data-testid="recent-chat-item">
              <ListItemButton
                onClick={() => handleChatClick(chat.supportChatId, previewText)}
                sx={{
                  py: 0.5,
                  px: 0.75,
                  borderRadius: 0.5,
                  minHeight: 'auto',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.8125rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {previewText}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.6875rem',
                          flexShrink: 0,
                          lineHeight: 1,
                        }}
                      >
                        {timeAgo}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Box>
  )
}
